/**
 * FERP Portal - DSL Editor Page (Issue #568 Phase 2.4)
 *
 * Architect-facing WHEN/THEN DSL editor for creating policy rules visually.
 * Compiles DSL expressions to Zen Engine JDM format.
 *
 * FERP Principles:
 * - Backend-driven: compileDsl / validateDsl mutations handle all logic
 * - Repository pattern: All data through GraphQL
 * - Life-critical: Compiled rules control approval routing
 * - Data-driven: Syntax help from getDslSyntaxHelp query
 */

import { useState, useCallback } from 'react';
import {
  Container,
  Title,
  Text,
  Group,
  Stack,
  Paper,
  Card,
  Button,
  Textarea,
  Alert,
  Badge,
  Code,
  Tabs,
  List,
  Accordion,
  Loader,
  SimpleGrid,
  CopyButton,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconCode,
  IconCheck,
  IconX,
  IconPlayerPlay,
  IconAlertTriangle,
  IconInfoCircle,
  IconCopy,
  IconBook,
  IconBolt,
} from '@tabler/icons-react';
import {
  useIsDslAvailableQuery,
  useGetDslSyntaxHelpQuery,
  useCompileDslMutation,
  useValidateDslMutation,
} from '@/generated/graphql';

// ============================================================================
// Types
// ============================================================================

interface DslError {
  message: string;
  kind: string;
  line: number | null;
  column: number | null;
  ferpCode: string;
}

interface CompilationResult {
  success: boolean;
  jdm: string | null;
  ast: string | null;
  ruleCount: number;
  inputFields: string[];
  errors: DslError[];
}

interface ValidationResult {
  valid: boolean;
  errors: DslError[];
  warnings: string[];
  ruleCount: number;
}

interface OperatorInfo {
  symbol: string;
  name: string;
  example: string;
}

interface DslExample {
  name: string;
  description: string;
  source: string;
}

// ============================================================================
// Component
// ============================================================================

export default function DslEditorPage() {
  const [source, setSource] = useState('');
  const [compilationResult, setCompilationResult] =
    useState<CompilationResult | null>(null);
  const [validationResult, setValidationResult] =
    useState<ValidationResult | null>(null);
  const [activeTab, setActiveTab] = useState<string | null>('editor');

  // Check DSL availability
  const { data: availabilityData, loading: availabilityLoading } = useIsDslAvailableQuery();

  // Load syntax help
  const { data: syntaxData } = useGetDslSyntaxHelpQuery();

  // Mutations
  const [compileDsl, { loading: compiling }] = useCompileDslMutation();
  const [validateDsl, { loading: validating }] = useValidateDslMutation();

  const handleCompile = useCallback(async () => {
    if (!source.trim()) return;
    setValidationResult(null);
    try {
      const { data } = await compileDsl({
        variables: { input: { source } },
      });
      if (data?.compileDsl) {
        setCompilationResult(data.compileDsl);
      }
    } catch (err) {
      setCompilationResult({
        success: false,
        jdm: null,
        ruleCount: 0,
        inputFields: [],
        errors: [
          {
            message: err instanceof Error ? err.message : 'Compilation failed',
            line: null,
            column: null,
            kind: 'INTERNAL',
            ferpCode: '',
          },
        ],
        ast: null,
      });
    }
  }, [source, compileDsl]);

  const handleValidate = useCallback(async () => {
    if (!source.trim()) return;
    setCompilationResult(null);
    try {
      const { data } = await validateDsl({
        variables: { input: { source } },
      });
      if (data?.validateDsl) {
        setValidationResult(data.validateDsl);
      }
    } catch (err) {
      setValidationResult({
        valid: false,
        errors: [
          {
            message: err instanceof Error ? err.message : 'Validation failed',
            line: null,
            column: null,
            kind: 'INTERNAL',
            ferpCode: '',
          },
        ],
        warnings: [],
        ruleCount: 0,
      });
    }
  }, [source, validateDsl]);

  const loadExample = useCallback((exampleSource: string) => {
    setSource(exampleSource);
    setCompilationResult(null);
    setValidationResult(null);
  }, []);

  const syntaxHelp = syntaxData?.getDslSyntaxHelp;
  const isAvailable = availabilityData?.isDslAvailable?.available ?? false;

  if (availabilityLoading) {
    return (
      <Container size="xl" py="xl">
        <Group justify="center" py="xl">
          <Loader />
          <Text>Checking DSL compiler availability...</Text>
        </Group>
      </Container>
    );
  }

  const errors = compilationResult?.errors ?? validationResult?.errors ?? [];
  const hasErrors = errors.length > 0;

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <Group>
            <IconCode size={28} />
            <div>
              <Title order={2}>DSL Rule Editor</Title>
              <Text size="sm" c="dimmed">
                WHEN/THEN policy rules compiled to Zen Engine
              </Text>
            </div>
          </Group>
          <Badge
            color={isAvailable ? 'green' : 'yellow'}
            variant="light"
            size="lg"
          >
            {isAvailable ? 'Compiler Available' : 'Stub Mode'}
          </Badge>
        </Group>

        {!isAvailable && (
          <Alert
            icon={<IconInfoCircle size={16} />}
            title="DSL Compiler Not Enabled"
            color="yellow"
          >
            {availabilityData?.isDslAvailable?.message ??
              'Build gateway with --features dsl to enable compilation.'}
          </Alert>
        )}

        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="editor" leftSection={<IconCode size={14} />}>
              Editor
            </Tabs.Tab>
            <Tabs.Tab value="reference" leftSection={<IconBook size={14} />}>
              Syntax Reference
            </Tabs.Tab>
            <Tabs.Tab value="examples" leftSection={<IconBolt size={14} />}>
              Examples
            </Tabs.Tab>
          </Tabs.List>

          {/* Editor Tab */}
          <Tabs.Panel value="editor" pt="md">
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
              {/* Left: Editor */}
              <Stack gap="sm">
                <Paper withBorder p="md">
                  <Stack gap="sm">
                    <Text fw={600} size="sm">
                      DSL Source
                    </Text>
                    <Textarea
                      placeholder={
                        'WHEN confidence >= 0.95 AND validation.violation_count == 0\nTHEN auto_approve PRIORITY 100 DEADLINE 24h'
                      }
                      value={source}
                      onChange={(e) => {
                        setSource(e.currentTarget.value);
                        setCompilationResult(null);
                        setValidationResult(null);
                      }}
                      minRows={12}
                      maxRows={20}
                      autosize
                      styles={{
                        input: {
                          fontFamily: 'monospace',
                          fontSize: '13px',
                        },
                      }}
                    />
                    <Group>
                      <Button
                        leftSection={<IconPlayerPlay size={14} />}
                        onClick={handleCompile}
                        loading={compiling}
                        disabled={!source.trim()}
                      >
                        Compile to JDM
                      </Button>
                      <Button
                        variant="outline"
                        leftSection={<IconCheck size={14} />}
                        onClick={handleValidate}
                        loading={validating}
                        disabled={!source.trim()}
                      >
                        Validate Only
                      </Button>
                    </Group>
                  </Stack>
                </Paper>
              </Stack>

              {/* Right: Results */}
              <Stack gap="sm">
                {/* Compilation Result */}
                {compilationResult && (
                  <Paper withBorder p="md">
                    <Stack gap="sm">
                      <Group>
                        {compilationResult.success ? (
                          <>
                            <IconCheck size={16} color="green" />
                            <Text fw={600} c="green" size="sm">
                              Compiled Successfully
                            </Text>
                            <Badge size="sm" variant="light">
                              {compilationResult.ruleCount} rule
                              {compilationResult.ruleCount !== 1 ? 's' : ''}
                            </Badge>
                          </>
                        ) : (
                          <>
                            <IconX size={16} color="red" />
                            <Text fw={600} c="red" size="sm">
                              Compilation Failed
                            </Text>
                          </>
                        )}
                      </Group>

                      {compilationResult.jdm && (
                        <div>
                          <Group justify="space-between" mb={4}>
                            <Text size="xs" fw={500}>
                              JDM Output (Zen Engine format)
                            </Text>
                            <CopyButton value={compilationResult.jdm}>
                              {({ copied, copy }) => (
                                <Tooltip
                                  label={copied ? 'Copied' : 'Copy JDM'}
                                >
                                  <ActionIcon
                                    size="xs"
                                    variant="subtle"
                                    onClick={copy}
                                  >
                                    <IconCopy size={12} />
                                  </ActionIcon>
                                </Tooltip>
                              )}
                            </CopyButton>
                          </Group>
                          <Code
                            block
                            style={{
                              maxHeight: 300,
                              overflow: 'auto',
                              fontSize: '11px',
                            }}
                          >
                            {(() => {
                              try {
                                return JSON.stringify(
                                  JSON.parse(compilationResult.jdm),
                                  null,
                                  2,
                                );
                              } catch {
                                return compilationResult.jdm;
                              }
                            })()}
                          </Code>
                        </div>
                      )}
                    </Stack>
                  </Paper>
                )}

                {/* Validation Result */}
                {validationResult && (
                  <Paper withBorder p="md">
                    <Group>
                      {validationResult.valid ? (
                        <>
                          <IconCheck size={16} color="green" />
                          <Text fw={600} c="green" size="sm">
                            Valid DSL
                          </Text>
                        </>
                      ) : (
                        <>
                          <IconX size={16} color="red" />
                          <Text fw={600} c="red" size="sm">
                            Invalid DSL
                          </Text>
                        </>
                      )}
                    </Group>
                  </Paper>
                )}

                {/* Errors */}
                {hasErrors && (
                  <Alert
                    icon={<IconAlertTriangle size={16} />}
                    title={`${errors.length} Error${errors.length !== 1 ? 's' : ''}`}
                    color="red"
                    variant="light"
                  >
                    <List size="sm" spacing={4}>
                      {errors.map((err, i) => (
                        <List.Item key={i}>
                          {err.line != null && (
                            <Badge size="xs" variant="outline" mr={4}>
                              Line {err.line}
                              {err.column != null ? `:${err.column}` : ''}
                            </Badge>
                          )}
                          {err.message}
                        </List.Item>
                      ))}
                    </List>
                  </Alert>
                )}

                {/* Placeholder when no results */}
                {!compilationResult && !validationResult && (
                  <Card withBorder p="xl" ta="center">
                    <Stack align="center" gap="sm">
                      <IconCode size={40} opacity={0.3} />
                      <Text c="dimmed" size="sm">
                        Write DSL rules and click Compile or Validate
                      </Text>
                    </Stack>
                  </Card>
                )}
              </Stack>
            </SimpleGrid>
          </Tabs.Panel>

          {/* Syntax Reference Tab */}
          <Tabs.Panel value="reference" pt="md">
            {syntaxHelp ? (
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                <Paper withBorder p="md">
                  <Stack gap="sm">
                    <Text fw={600}>Operators</Text>
                    {syntaxHelp.operators?.map(
                      (op: OperatorInfo, i: number) => (
                        <Group key={i} gap="xs">
                          <Code>{op.symbol}</Code>
                          <Text size="sm">{op.name}</Text>
                          <Text size="xs" c="dimmed">
                            {op.example}
                          </Text>
                        </Group>
                      ),
                    )}
                  </Stack>
                </Paper>

                <Stack gap="md">
                  <Paper withBorder p="md">
                    <Stack gap="sm">
                      <Text fw={600}>Built-in Fields</Text>
                      <Group gap={4}>
                        {syntaxHelp.builtinFields?.map(
                          (field: string, i: number) => (
                            <Badge key={i} size="sm" variant="light">
                              {field}
                            </Badge>
                          ),
                        )}
                      </Group>
                    </Stack>
                  </Paper>

                  <Paper withBorder p="md">
                    <Stack gap="sm">
                      <Text fw={600}>Built-in Actions</Text>
                      <Group gap={4}>
                        {syntaxHelp.builtinActions?.map(
                          (action: string, i: number) => (
                            <Badge key={i} size="sm" variant="light" color="blue">
                              {action}
                            </Badge>
                          ),
                        )}
                      </Group>
                    </Stack>
                  </Paper>
                </Stack>

                <Paper withBorder p="md" style={{ gridColumn: '1 / -1' }}>
                  <Accordion>
                    <Accordion.Item value="full-reference">
                      <Accordion.Control>
                        Full Syntax Reference (v{syntaxHelp.version})
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Code
                          block
                          style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}
                        >
                          {syntaxHelp.syntaxReference}
                        </Code>
                      </Accordion.Panel>
                    </Accordion.Item>
                  </Accordion>
                </Paper>
              </SimpleGrid>
            ) : (
              <Loader />
            )}
          </Tabs.Panel>

          {/* Examples Tab */}
          <Tabs.Panel value="examples" pt="md">
            {syntaxHelp?.examples ? (
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                {syntaxHelp.examples.map((ex: DslExample, i: number) => (
                  <Card key={i} withBorder>
                    <Stack gap="sm">
                      <Group justify="space-between">
                        <Text fw={600} size="sm">
                          {ex.name}
                        </Text>
                        <Button
                          size="xs"
                          variant="light"
                          onClick={() => {
                            loadExample(ex.source);
                            setActiveTab('editor');
                          }}
                        >
                          Load
                        </Button>
                      </Group>
                      <Text size="xs" c="dimmed">
                        {ex.description}
                      </Text>
                      <Code
                        block
                        style={{
                          fontFamily: 'monospace',
                          fontSize: '12px',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {ex.source}
                      </Code>
                    </Stack>
                  </Card>
                ))}
              </SimpleGrid>
            ) : (
              <Loader />
            )}
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}
